import type {
  ObsBilingual,
  ObsBuiltinCard,
  ObsBuiltinSpec,
  ObsCardSpec,
  ObsCapabilities,
  ObsChartKind,
  ObsChartLayout,
  ObsChartSpec,
  ObsDim,
  ObsMeasure,
} from "@/types/obs";

/**
 * Naming charts in the picker, and suggesting a heading for each one.
 *
 * 🔑 THE SUGGESTED HEADING USES THE OPERATOR'S OWN COLUMN NAMES. The public
 * screens fall back to generic dimension words — «الجهة» — because at render time
 * all they have is the parsed dataset. Here we still hold the mapping, so the
 * suggestion can read «الوزارة او الهيئة», which is what the six say and what the
 * operator actually calls it.
 *
 * That is also why the suggestion is written into the field as a VALUE rather
 * than shown as a placeholder: what the operator sees is then exactly what ships,
 * and the site never regenerates something different from what they approved.
 */

export const KIND_LABELS: Record<ObsChartKind, { ar: string; note: string }> = {
  bar: { ar: "أعمدة", note: "مقارنة رأسية" },
  groupedBar: { ar: "أعمدة مزدوجة", note: "المصدق والفعلي جنبًا إلى جنب" },
  horizontalBar: { ar: "أعمدة أفقية", note: "أفضل للأسماء الطويلة" },
  line: { ar: "خط زمني", note: "تطوّر عبر السنوات" },
  combo: { ar: "أعمدة مع خط النسبة", note: "المصدق والفعلي مع نسبة التنفيذ" },
  donut: { ar: "دائري", note: "توزيع نسبي — للفئات القليلة" },
  treemap: { ar: "خريطة تجميعية", note: "مربعات بحجم القيمة" },
  table: { ar: "جدول تفصيلي", note: "كل الأرقام كاملة" },
};

const DIM_FALLBACK: Record<ObsDim, ObsBilingual> = {
  year: { ar: "السنة المالية", en: "Fiscal year" },
  type: { ar: "النوع", en: "Type" },
  category: { ar: "التصنيف الاقتصادي", en: "Economic category" },
  entity: { ar: "الجهة", en: "Entity" },
};

const MEASURE_LABELS: Record<ObsMeasure, ObsBilingual> = {
  budget: { ar: "المصدق", en: "Approved" },
  actual: { ar: "الفعلي", en: "Actual" },
  rate: { ar: "نسبة التنفيذ", en: "Execution rate" },
};

/**
 * Is this entry one of the screen's OWN cards?
 *
 * 🔑 DEFINED HERE, NOT IN `types/obs.ts`. That module is types only, and a value
 * export in it cannot be imported by `scripts/check-obs-chart-layout.mjs`, which
 * runs under plain node where the `@/` alias does not resolve. Keeping the guard
 * beside the packer it guards is what lets the gate execute the real code instead
 * of re-implementing it.
 */
export function isBuiltinSpec(spec: ObsCardSpec): spec is ObsBuiltinSpec {
  return typeof (spec as ObsBuiltinSpec).builtin === "string";
}

/** Column names the operator mapped, per dimension. */
export type DimHeaders = Partial<Record<ObsDim, ObsBilingual>>;

/**
 * Build the display name for each dimension from the confirmed mapping.
 *
 * The English side comes from `enRoles` when the workbook carried an English
 * column; where it did not, the fallback is a generic English word rather than
 * the Arabic header — putting Arabic in the English title is the leak this whole
 * feature is careful about.
 */
export function dimHeadersFromMapping(
  roles: Partial<Record<string, string>>,
  enRoles: Partial<Record<string, string>> = {},
): DimHeaders {
  const out: DimHeaders = {};
  for (const dim of ["year", "type", "category", "entity"] as ObsDim[]) {
    const ar = roles[dim];
    if (!ar) continue;
    const en = enRoles[dim];
    out[dim] = {
      ar,
      // ALL-CAPS is how these headers are written in the workbooks; title case
      // reads better in a heading.
      en: en ? titleCase(en) : DIM_FALLBACK[dim].en,
    };
  }
  return out;
}

function titleCase(s: string): string {
  return s
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w.length > 2 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function measuresForKind(
  kind: ObsChartKind,
  caps: ObsCapabilities,
): ObsMeasure[] {
  const has = (m: ObsMeasure) => caps.measures.includes(m);
  if (kind === "groupedBar" || kind === "combo") {
    return (["budget", "actual"] as ObsMeasure[]).filter(has);
  }
  if (kind === "table") {
    return (["budget", "actual", "rate"] as ObsMeasure[]).filter(has);
  }
  return [has("actual") ? "actual" : has("budget") ? "budget" : "rate"];
}

/** The heading the wizard offers for a chart, in both languages. */
export function suggestTitle(
  spec: Pick<ObsChartSpec, "kind" | "dim">,
  caps: ObsCapabilities,
  headers: DimHeaders,
): ObsBilingual {
  const dim = headers[spec.dim] ?? DIM_FALLBACK[spec.dim];
  const measures = measuresForKind(spec.kind, caps).filter((m) => m !== "rate");

  if (!measures.length) {
    return {
      ar: `نسبة التنفيذ حسب ${dim.ar}`,
      en: `Execution rate by ${dim.en}`,
    };
  }
  const ar = measures.map((m) => MEASURE_LABELS[m].ar).join(" و");
  const en = measures.map((m) => MEASURE_LABELS[m].en).join(" and ");
  return { ar: `${ar} حسب ${dim.ar}`, en: `${en} by ${dim.en}` };
}

export function dimLabel(dim: ObsDim, headers: DimHeaders): string {
  return headers[dim]?.ar ?? DIM_FALLBACK[dim].ar;
}

/**
 * Charts are publishable only when every title carries BOTH languages.
 *
 * Mirrors the server's rule rather than replacing it — the backend rejects a
 * half-filled title too. This one exists so the operator finds out while the
 * field is in front of them, not after pressing publish.
 */
export function chartsComplete(charts: ObsCardSpec[]): boolean {
  // A built-in card has no title to fill in — the screen owns its heading.
  return charts
    .filter((c): c is ObsChartSpec => !isBuiltinSpec(c))
    .every((c) => Boolean(c.title?.ar?.trim() && c.title?.en?.trim()));
}

/* ------------------------------------------------------------------ layout */

export const LAYOUT_LABELS: Record<
  ObsChartLayout,
  { ar: string; note: string }
> = {
  row: { ar: "صف", note: "نصف العرض، بجانب الرسم التالي" },
  column: { ar: "عمود", note: "بالعرض الكامل، وحده في سطره" },
};

/** What the operator chose, with the default made explicit. */
export function layoutOf(spec: ObsCardSpec): ObsChartLayout {
  return spec.layout === "row" ? "row" : "column";
}

/**
 * The heading a card shows in the picker.
 *
 * A built-in card's title comes from the server, because the SCREEN owns the
 * wording — «تركيبة الدين العام», not `composition`. An added chart carries its
 * own operator-written title.
 */
export function cardLabel(
  spec: ObsCardSpec,
  builtins: ObsBuiltinCard[],
  headers: DimHeaders,
): { title: string; note: string } {
  if (isBuiltinSpec(spec)) {
    const card = builtins.find((b) => b.id === spec.builtin);
    return {
      title: card?.title.ar ?? spec.builtin,
      note: "بطاقة أصلية من تصميم هذه الشاشة — يمكن ترتيبها أو إخفاؤها، لا تعديل محتواها",
    };
  }
  return {
    title: `${KIND_LABELS[spec.kind].ar} — حسب ${dimLabel(spec.dim, headers)}`,
    note: KIND_LABELS[spec.kind].note,
  };
}

export interface ObsPlacement {
  spec: ObsCardSpec;
  full: boolean;
}

/**
 * Resolve the chart list into card widths, exactly as the public screen will.
 *
 * ⚠️ MIRRORS `packRows` in imof `src/lib/obs/compose.ts`, which is the copy that
 * actually renders the page. Two repos cannot share a module, so the duplication
 * is explicit and each copy names the other; both gates
 * (`scripts/check-obs-chart-layout.mjs`, in BOTH repos) ask the SAME case table,
 * so a divergence turns one of them red.
 *
 * This copy exists only so the picker can tell the operator what their choices
 * will do BEFORE they publish. The preview link on the last step is the real
 * screen, so the site is never rendered from this — the worst outcome of drift
 * here is a wrong hint, not a wrong page.
 *
 * 🔑 TWO PER ROW IS STRUCTURAL. This cannot put a third card in a row, which is
 * why there is no "maximum reached" error anywhere in the picker: a third chart
 * marked «صف» simply takes the full width, and the hint under it says so.
 */
export function packRows(specs: ObsCardSpec[]): ObsPlacement[] {
  const out: ObsPlacement[] = [];
  for (let i = 0; i < specs.length; i += 1) {
    const spec = specs[i];
    const next = specs[i + 1];
    if (layoutOf(spec) === "row" && next && layoutOf(next) === "row") {
      out.push({ spec, full: false }, { spec: next, full: false });
      i += 1;
      continue;
    }
    out.push({ spec, full: true });
  }
  return out;
}

/**
 * Reconstruct the rows the CSS grid will lay out.
 *
 * `.obs-grid` is two columns and a full-width card spans both, so a full card
 * owns its row and half cards fill the columns in order. Rebuilding that here —
 * rather than reading pair boundaries back out of a flat list — is what makes the
 * partner lookup below correct across two ADJACENT pairs, where the neighbouring
 * half card belongs to the next row, not this one.
 */
function gridRows(packed: ObsPlacement[]): ObsPlacement[][] {
  const rows: ObsPlacement[][] = [];
  let open: ObsPlacement[] = [];
  for (const p of packed) {
    if (p.full) {
      if (open.length) rows.push(open);
      rows.push([p]);
      open = [];
      continue;
    }
    open.push(p);
    if (open.length === 2) {
      rows.push(open);
      open = [];
    }
  }
  if (open.length) rows.push(open);
  return rows;
}

/**
 * The sentence under each chart saying where it will actually land, indexed the
 * same way as the chart list.
 *
 * Written from the PACKED result rather than from the chart's own choice, so a
 * chart marked «صف» with no partner says the true thing — «بالعرض الكامل» — and
 * the operator learns the two-per-row rule by reading what will happen instead of
 * by hitting an error.
 */
export function placementNotes(specs: ObsCardSpec[]): string[] {
  const notes: string[] = [];
  for (const row of gridRows(packRows(specs))) {
    for (const p of row) {
      if (p.full) {
        notes.push(
          layoutOf(p.spec) === "row"
            ? "لا يوجد رسم «صف» يليه، لذا سيظهر بالعرض الكامل"
            : "سيظهر بالعرض الكامل، وحده في سطره",
        );
        continue;
      }
      const partner = row.find((other) => other !== p)?.spec;
      // A built-in card's heading lives on the server, not in the spec, so a
      // partner that is one gets the generic wording rather than its id.
      const name =
        partner && !isBuiltinSpec(partner)
          ? partner.title?.ar?.trim()
          : undefined;
      notes.push(
        name
          ? `سيظهر بنصف العرض بجانب «${name}»`
          : "سيظهر بنصف العرض بجانب الرسم المجاور",
      );
    }
  }
  return notes;
}
